
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import { sideFragmentShaderString } from '@/shaders/sideFragment'
import References from '@/singletons/references'
import sideSettings from '@/settings/side-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselSide',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const configSideId = computed(() => store.getters.configSideId)
    const configThreadId = computed(() => store.getters.configThreadId)
    const isRainbowConfig = computed(() => store.getters.isRainbowConfig)
    const isRenderer = inject('isRenderer') as boolean
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService
    let currentTexture = ''
    let currentItoTexture = ''
    let currentNormal = ''

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
        try {
          initTextureLoadService()
          let mat = materialCreateService.createMaterial(sideFragmentShaderString, true, false, isRenderer)
          mat.uniforms.itoColor = { value: materialCreateService.createColor(0xFFFFFF) }
          mat.uniforms.sideOnly = { value: 0 }
          mat.uniforms.sideTexture = { value: null }
          mat.uniforms.sideItoTexture = { value: null }
          mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
          mat.uniforms.roughness.value = materialCreateService.getRoughness()
          mat.uniforms.metalness.value = materialCreateService.getMetalness()
          References.instance.setSideMaterial(mat)
        }
        catch(err) {
          SentrySingleton.instance.captureException(err)
        }
      }
    )

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    // prettier-ignore
    watch(() => isBagLoaded.value, (_) => { References.instance.getBagReferences().side.material = References.instance.getSideMaterial() })
    // prettier-ignore
    watch(() => configSideId.value, (_) => { updateOption() })

    function updateOption() {
      try {
        if (configSideId.value) {
          setSide(sideSettings.sideTextures.find((x: any) => x.id == configSideId.value))
        } else {
          setSide(sideSettings.sideTextures.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setSide(obj: any) {
      initTextureLoadService()
      loadTexture(obj ? obj.diffuse : null)
      loadItoTexture(obj ? (isRainbowConfig.value && obj.rainbowTexture ? obj.rainbowTexture : obj.itoTexture) : null)
      loadNormal(obj ? obj.normal : null)
    }

    function loadTexture(path: string | null) {
      if (path) {
        if (path != currentTexture) {
          currentTexture = path
          textureLoadService.loadImage(path, (tex: THREE.Texture | null) => {
            if (References.instance.getSideMaterial().uniforms.sideTexture.value) {
              ;(References.instance.getSideMaterial().uniforms.sideTexture.value as THREE.Texture).dispose()
            }
            References.instance.getSideMaterial().uniforms.sideTexture.value = tex
          })
        }
      } else {
        References.instance.getSideMaterial().uniforms.sideTexture = { value: null }
      }
    }

    function loadItoTexture(path: string | null) {
      if (path) {
        if (path != currentItoTexture) {
          currentItoTexture = path
          textureLoadService.loadImage(path, (tex: THREE.Texture | null) => {
            References.instance.getSideMaterial().uniforms.sideItoTexture.value = tex
          })
        }
      } else {
        References.instance.getSideMaterial().uniforms.sideItoTexture = { value: null }
      }
    }

    function loadNormal(path: string | null | undefined) {
      const desiredPath = path ?? 'side/default_n'
      if (path != currentNormal) {
        currentNormal = desiredPath
        textureLoadService.loadImage(desiredPath, (tex: THREE.Texture | null) => {
          References.instance.getSideMaterial().uniforms.normalMap.value = tex
        })
      }
    }

    // prettier-ignore
    watch(() => configThreadId.value, (_) => {
      try {
        if(configSideId.value) {
          setSide(sideSettings.sideTextures.find((x: any) => x.id == configSideId.value))
        }
      }
      catch(err) {
        SentrySingleton.instance.captureException(err)
      }
    })

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isBagLoaded,
      isSceneLoaded,

      configSideId,
      configThreadId,
      isRainbowConfig
    }
  }
})
